import React from 'react';
import { Form, FormControl } from 'react-bootstrap';

import Checkbox from 'components/DesignSystem/Checkbox';
import Icon from 'components/DesignSystem/Icon';
import RadioButton from 'components/DesignSystem/RadioButton';
import { validateWeightingFactorsFormat } from 'helper/flux/weightingFactors';
import useNewStudyForm from 'utils/hooks/flux/personalized/useNewStudyForm';
import { cssVar } from 'constants/theme';

const LABEL_VAZAO_MEDIA_PONDERADA = 'Produzir vazão média ponderada';
const LABEL_SOMENTE_PROBABILISTICOS = 'Produzir somente cenários probabilísticos';
const LABEL_RESUMO_MULTICENARIOS = 'Produzir resumo multicenários';

function ScenarioCheckbox(
  label: string,
  checked: boolean,
  onChange: () => void,
) {
  return (
    <Checkbox
      label={label}
      key={label}
      onChange={onChange}
      checked={checked}
    />
  );
}
function ScenarioRadioButton(
  label: string,
  checked: boolean,
  onChange: () => void,
) {
  return (
    <div className="pl-3">
      <RadioButton
        name="dimensions"
        label={label}
        key={label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
}

function AutomaticFlowPercentile() {
  const {
    flowPercentileValue,
    setPercentileValues,
    multiScenarios,
    toggleMultiScenario,
  } = useNewStudyForm();
  const optionLabel = 'Produzir percentis de vazão';
  const setValue = (event: any) => setPercentileValues(event.target.value);
  const isChecked = multiScenarios.includes(optionLabel);
  const displayClass = isChecked ? 'd-block' : 'd-none';

  return (
    <>
      {ScenarioCheckbox(
        optionLabel,
        isChecked,
        () => { toggleMultiScenario(optionLabel); },
      )}
      <div className={`ps-5 ${displayClass}`}>
        <Form.Control value={flowPercentileValue} onChange={setValue} />
      </div>
    </>
  );
}

function AutomaticFlowByGrouping() {
  const {
    multiScenarios,
    toggleMultiScenario,
    toggleMultiScenarioVersusOption,
    groupNumber,
    setGroupNumber,
  } = useNewStudyForm();
  const radioButtonLabels = [
    'SE+NE versus S+N',
    'SE+S versus NE+N',
    'SE+N versus S+NE',
    'SE versus S+NE+N',
    'SE vs S vs NE vs N'];

  const shouldDisplayGroupingDimensions = multiScenarios.includes('Produzir vazão por agrupamento:');

  const shouldDisplayRadioOptions = shouldDisplayGroupingDimensions
    && multiScenarios.includes('Definir dimensões para agrup.');

  const optionLabel = 'Produzir vazão por agrupamento:';

  return (
    <>
      {ScenarioCheckbox(
        optionLabel,
        multiScenarios.includes(optionLabel),
        () => { toggleMultiScenario(optionLabel); },
      )}
      { shouldDisplayGroupingDimensions
        && (
          <div className="ps-3">
            {ScenarioCheckbox(
              'Predefinir número de agrupamentos',
              multiScenarios.includes('Predefinir número de agrupamentos'),
              () => { toggleMultiScenario('Predefinir número de agrupamentos'); },
            )}
            { multiScenarios.includes('Predefinir número de agrupamentos')
              && (
                <div className="ps-5">
                  <FormControl
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={groupNumber && !Number.isNaN(groupNumber) ? groupNumber : 0}
                    onChange={(e) => {
                      setGroupNumber(parseInt(e.target.value || '0', 10));
                    }}
                  />
                </div>
              ) }
            {ScenarioCheckbox(
              'Definir dimensões para agrup.',
              multiScenarios.includes('Definir dimensões para agrup.'),
              () => { toggleMultiScenario('Definir dimensões para agrup.'); },
            )}
          </div>
        )}
      { shouldDisplayRadioOptions && radioButtonLabels
        .map((label) => (
          <div className="ps-5" key={label}>
            { ScenarioRadioButton(
              label,
              multiScenarios.includes(label),
              () => toggleMultiScenarioVersusOption(label),
            ) }
          </div>
        ))}
    </>
  );
}

function WeightedAverageFlow() {
  const {
    multiScenarios,
    toggleMultiScenario,
    weightingFactorsText,
    setWeightingFactorsText,
  } = useNewStudyForm();
  const isChecked = multiScenarios.includes(LABEL_VAZAO_MEDIA_PONDERADA);
  const formatResult = validateWeightingFactorsFormat(weightingFactorsText);
  const showInvalid = isChecked && !formatResult.valid;
  const displayClass = isChecked ? 'd-block' : 'd-none';

  return (
    <>
      <div className={showInvalid ? 'text-danger' : ''}>
        {ScenarioCheckbox(
          LABEL_VAZAO_MEDIA_PONDERADA,
          isChecked,
          () => { toggleMultiScenario(LABEL_VAZAO_MEDIA_PONDERADA); },
        )}
      </div>
      <div className={`ps-5 ${displayClass}`}>
        <div className="position-relative">
          <Form.Control
            type="text"
            value={weightingFactorsText}
            onChange={(e) => setWeightingFactorsText(e.target.value)}
            className={showInvalid ? 'border-danger pe-4' : ''}
          />
          {showInvalid && (
            <span className="position-absolute top-50 end-0 translate-middle-y me-2">
              <Icon iconType="warning" color={cssVar('danger-fg')} size="small" />
            </span>
          )}
        </div>
        {!showInvalid && (
          <Form.Text className="text-muted small d-block mt-1">
            Fatores separados por vírgula. Num. fatores = num. cenários; cada fator entre 0 e 1;
            soma = 1.
          </Form.Text>
        )}
        {showInvalid && !formatResult.valid && 'message' in formatResult && (
          <Form.Text className="text-danger small d-block mt-1">
            {formatResult.message}
          </Form.Text>
        )}
      </div>
    </>
  );
}

export default function MultiScenarios() {
  const {
    multiScenarios,
    toggleMultiScenario,
  } = useNewStudyForm();

  return (
    <div>
      {ScenarioCheckbox(
        'Produzir vazão média',
        multiScenarios.includes('Produzir vazão média'),
        () => { toggleMultiScenario('Produzir vazão média'); },
      )}
      {WeightedAverageFlow()}
      {AutomaticFlowPercentile()}
      {AutomaticFlowByGrouping()}
      {ScenarioCheckbox(
        LABEL_SOMENTE_PROBABILISTICOS,
        multiScenarios.includes(LABEL_SOMENTE_PROBABILISTICOS),
        () => { toggleMultiScenario(LABEL_SOMENTE_PROBABILISTICOS); },
      )}
      {ScenarioCheckbox(
        LABEL_RESUMO_MULTICENARIOS,
        multiScenarios.includes(LABEL_RESUMO_MULTICENARIOS),
        () => { toggleMultiScenario(LABEL_RESUMO_MULTICENARIOS); },
      )}
    </div>
  );
}
